/**
 * Centralized validation constants and utilities for image attachments.
 * Used by both file selector and paste handler to ensure consistent validation rules.
 */

/**
 * Image validation configuration constants.
 * These rules apply to both file selector uploads and pasted images.
 */
export const IMAGE_VALIDATION = {
  /** Allowed MIME types for image uploads */
  allowedTypes: ['image/jpeg', 'image/png'] as const,
  /** Allowed file extensions for image uploads */
  allowedExtensions: ['jpg', 'jpeg', 'png'] as const,
  /** Maximum file size in bytes (10 MB) */
  maxSize: 10 * 1024 * 1024,
  /** Maximum number of images per message */
  maxCount: 10,
} as const;

export const DOCUMENT_VALIDATION = {
  allowedTypes: [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/markdown',
    'text/x-markdown',
    'text/plain',
  ] as const,
  allowedExtensions: ['pdf', 'docx', 'md', 'markdown', 'txt'] as const,
  maxSize: 2 * 1024 * 1024, // 2MB
  maxCount: 5,
} as const;

export const SPREADSHEET_VALIDATION = {
  allowedTypes: [
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ] as const,
  allowedExtensions: ['csv', 'xlsx'] as const,
  maxSize: 10 * 1024 * 1024, // 10MB
  maxCount: 5,
} as const;

const ALLOWED_SPREADSHEET_MIME_TYPES = new Set<string>(SPREADSHEET_VALIDATION.allowedTypes);
const ALLOWED_SPREADSHEET_EXTENSIONS = new Set<string>(SPREADSHEET_VALIDATION.allowedExtensions);

export function isAllowedSpreadsheetMimeType(fileType: string | undefined | null): boolean {
  const normalized = (fileType ?? '').trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return ALLOWED_SPREADSHEET_MIME_TYPES.has(normalized);
}

export function isAllowedSpreadsheetExtension(extension: string | undefined | null): boolean {
  const normalized = (extension ?? '').trim().toLowerCase().replace(/^\./, '');
  if (!normalized) {
    return false;
  }
  return ALLOWED_SPREADSHEET_EXTENSIONS.has(normalized);
}

const IMAGE_MIME_TO_EXTENSIONS: Record<string, readonly string[]> = {
  'image/jpeg': ['jpg', 'jpeg'],
  'image/png': ['png'],
} as const;

const ALLOWED_IMAGE_EXTENSIONS = new Set<string>(IMAGE_VALIDATION.allowedExtensions);
const ALLOWED_IMAGE_MIME_TYPES = new Set<string>(IMAGE_VALIDATION.allowedTypes);

function normalizeExtension(extension: string | undefined | null): string {
  return (extension ?? '').trim().toLowerCase().replace(/^\./, '');
}

export function getImageExtensionFromFileName(fileName: string | undefined | null): string {
  if (!fileName) {
    return '';
  }
  const parts = fileName.split('.');
  if (parts.length < 2) {
    return '';
  }
  const ext = parts.pop();
  return normalizeExtension(ext);
}

export function normalizeImageUploadExtension(
  extension: string | undefined | null,
  fileName: string | undefined | null
): string {
  const ext = normalizeExtension(extension);
  if (ext) {
    return ext;
  }
  return getImageExtensionFromFileName(fileName);
}

/**
 * Determines whether a File object should be treated as an image upload.
 * Handles cases where the browser reports an unknown MIME type by falling back to extension check.
 */
export function isImageFile(file: File): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase();
  const normalizedType = file.type.toLowerCase();
  const hasUnknownType = normalizedType.length === 0 || normalizedType === 'application/octet-stream';
  return normalizedType.startsWith('image/') || (hasUnknownType && isAllowedImageExtension(ext));
}

export function isAllowedImageExtension(extension: string | undefined | null): boolean {
  const normalized = normalizeExtension(extension);
  if (!normalized) {
    return false;
  }
  return ALLOWED_IMAGE_EXTENSIONS.has(normalized);
}

export function isAllowedImageMimeType(fileType: string | undefined | null): boolean {
  const normalized = (fileType ?? '').trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return ALLOWED_IMAGE_MIME_TYPES.has(normalized);
}

export function getDefaultImageExtensionForMimeType(fileType: string | undefined | null): string {
  const normalizedType = (fileType ?? '').trim().toLowerCase();
  const allowedExtensions = IMAGE_MIME_TO_EXTENSIONS[normalizedType];
  return allowedExtensions?.[0] ?? '';
}

/**
 * Validates the count of images being added against the maximum allowed.
 * Calculates how many images can be accepted and how many exceed the limit.
 *
 * @param currentCount - Number of images already attached
 * @param newCount - Number of images being added
 * @param maxCount - Maximum allowed images per message
 * @returns Validation result with allowed and exceeded counts
 */
export function validateImageCount(
  currentCount: number,
  newCount: number,
  maxCount: number
): { isValid: boolean; allowedCount: number; exceededCount: number } {
  const totalCount = currentCount + newCount;

  if (totalCount <= maxCount) {
    return {
      isValid: true,
      allowedCount: newCount,
      exceededCount: 0,
    };
  }

  const remainingCapacity = Math.max(0, maxCount - currentCount);
  const exceededCount = newCount - remainingCapacity;

  return {
    isValid: false,
    allowedCount: remainingCapacity,
    exceededCount,
  };
}
